import { GPSTrack, SearchPredicate } from '@nx-smartmonkey/shared/domain';
import { PaginatedResult } from '@nx-smartmonkey/shared/interfaces';

import { Driver } from '../../domain/driver/Driver';
import { DriverStatus } from '../../domain/driver/DriverStatus';
import { GeoFence } from '../../domain/geoFence/GeoFence';
import { PlanFlat } from '../../domain/plan/PlanFlat';
import { Stop } from '../../domain/stop/Stop';
import { PlanActionType, PlanActions } from './actions';
import { ProjectPlansTableColumn, getProjectPlansTableColumns, setProjectPlansTableColumns } from './tableColumns';

function setActivePlanMapSymbolFilters({ symbols }: { symbols: MapFiltersSymbols }) {
  localStorage.setItem(`ACTIVE_PLAN_MAP_SYMBOL_FILTERS`, JSON.stringify(symbols));
}

function getActivePlanMapSymbolFilters() {
  const symbols = localStorage.getItem(`ACTIVE_PLAN_MAP_SYMBOL_FILTERS`);
  if (!symbols) {
    return defaultMapSymbolFilters;
  }
  return JSON.parse(symbols);
}

export type MapFiltersDrivers = Record<string, boolean>;

export type MapFiltersSymbols = {
  markers: boolean;
  lines: boolean;
  alerts: boolean;
  zones: boolean;
  arrows: boolean;
};

const defaultMapSymbolFilters: MapFiltersSymbols = {
  markers: true,
  lines: true,
  alerts: true,
  zones: true,
  arrows: false,
};

export type OptimizationStatus = `success` | `failed` | `canceled` | `running`;

export type OptimizationData = {
  status?: OptimizationStatus;
  startedAt?: Date;
};

export type MapFilters = {
  drivers: MapFiltersDrivers;
  symbols: MapFiltersSymbols;
};

export interface PlanState {
  plans: PaginatedResult<PlanFlat>;
  predicates: Array<SearchPredicate>;
  predicatesBase64: string;
  flatPlans: Array<PlanFlat>;
  activePlan: {
    plan?: PlanFlat;
    stops: Array<Stop>;
    stopsMap: Record<string, Stop>;
    drivers: Array<Driver>;
    driversMap: Record<string, Driver>;
    geoFences: Array<GeoFence>;
    geoFencesMap: Record<string, GeoFence>;
    mapFilters: MapFilters;
    optimization: OptimizationData;
    editingGeoFence?: GeoFence;
    focusedStop?: Stop;
  };
  tableColumns: Array<ProjectPlansTableColumn>;
}

export const getInitialState = (): PlanState => {
  return {
    plans: {
      limit: 20,
      offset: 0,
      total: 0,
      docs: [],
    },
    predicates: [],
    predicatesBase64: ``,
    flatPlans: [],
    activePlan: {
      plan: undefined,
      stops: [],
      stopsMap: {},
      drivers: [],
      driversMap: {},
      geoFences: [],
      geoFencesMap: {},
      mapFilters: {
        drivers: {
          [`unassigned`]: true,
        },
        symbols: getActivePlanMapSymbolFilters(),
      },
      optimization: {
        status: undefined,
        startedAt: undefined,
      },
      editingGeoFence: undefined,
      focusedStop: undefined,
    },
    tableColumns: getProjectPlansTableColumns(),
  };
};

const arrayToMap = (elements: Array<Record<string, any>>): Record<string, any> => {
  const map = {} as Record<string, any>;
  elements.forEach((element) => {
    map[element.id] = element;
  });
  return map;
};

export const PlanReducer = (state: PlanState, action: PlanActions): PlanState => {
  switch (action.type) {
    case PlanActionType.SET_ACTIVE_PLAN_EDITING_GEO_FENCE:
      return {
        ...state,
        activePlan: {
          ...state.activePlan,
          editingGeoFence: action.payload.editingGeoFence,
        },
      };
    case PlanActionType.SET_ACTIVE_PLAN_OPTIMIZATION_DATA:
      return {
        ...state,
        activePlan: {
          ...state.activePlan,
          optimization: {
            status: action.payload.status,
            startedAt: action.payload.startedAt,
          },
        },
      };
    case PlanActionType.SET_SEARCH_PLANS_PREDICATES: {
      const { predicates, predicatesBase64 } = action.payload;
      return {
        ...state,
        predicates,
        predicatesBase64,
      };
    }
    case PlanActionType.SET_PROJECT_PLANS_TABLE_COLUMNS:
      setProjectPlansTableColumns(action.payload);
      return {
        ...state,
        tableColumns: action.payload,
      };
    case PlanActionType.SEARCH_PLANS_SUCCESS:
      return {
        ...state,
        plans: action.payload.plans,
        tableColumns: getProjectPlansTableColumns(),
      };
    case PlanActionType.SEARCH_FLAT_PLANS_SUCCESS:
      return {
        ...state,
        flatPlans: action.payload.flatPlans,
      };
    case PlanActionType.SET_ACTIVE_PLAN:
      return {
        ...state,
        activePlan: {
          ...state.activePlan,
          plan: action.payload.activePlan,
        },
      };
    case PlanActionType.SET_ACTIVE_PLAN_DRIVERS: {
      const planDrivers = action.payload.planDrivers;
      const driversMap = arrayToMap(planDrivers);
      return {
        ...state,
        activePlan: {
          ...state.activePlan,
          drivers: planDrivers,
          driversMap,
        },
      };
    }
    case PlanActionType.SET_ACTIVE_PLAN_STOPS: {
      const planStops = action.payload.planStops;
      const stopsMap = arrayToMap(planStops);
      return {
        ...state,
        activePlan: {
          ...state.activePlan,
          stops: planStops,
          stopsMap,
        },
      };
    }
    case PlanActionType.SET_ACTIVE_PLAN_GEO_FENCES: {
      const planGeoFences = action.payload.planGeoFences;
      const geoFencesMap = arrayToMap(planGeoFences);
      return {
        ...state,
        activePlan: {
          ...state.activePlan,
          geoFences: planGeoFences,
          geoFencesMap,
        },
      };
    }
    case PlanActionType.UPDATE_PLAN_DRIVER: {
      const planDriver = action.payload.driver as Driver;

      if (!(planDriver.id in state.activePlan.driversMap)) return state;

      const newDriversMap = { ...state.activePlan.driversMap };
      newDriversMap[planDriver.id] = planDriver;
      const newDrivers = Object.values(newDriversMap);
      return {
        ...state,
        activePlan: {
          ...state.activePlan,
          drivers: newDrivers,
          driversMap: newDriversMap,
        },
      };
    }
    case PlanActionType.UPDATE_PLAN_STOP: {
      const planStop = action.payload.stop as Stop;

      if (!(planStop.id in state.activePlan.stopsMap)) return state;

      const newStopsMap = { ...state.activePlan.stopsMap };
      newStopsMap[planStop.id] = planStop;
      const newStops = Object.values(newStopsMap);

      // If updated stop is assigned to a driver, updates its array
      if (planStop.driver_id) {
        const newDriversMap = { ...state.activePlan.driversMap };

        if (newDriversMap[planStop.driver_id]) {
          newDriversMap[planStop.driver_id].stops = newDriversMap[planStop.driver_id].stops.map((driverStop) => {
            if (driverStop.id === planStop.id) {
              return planStop;
            }
            return driverStop;
          });
        }

        const newDrivers = Object.values(newDriversMap);

        return {
          ...state,
          activePlan: {
            ...state.activePlan,
            stops: newStops,
            stopsMap: newStopsMap,
            drivers: newDrivers,
            driversMap: newDriversMap,
          },
        };
      }

      return {
        ...state,
        activePlan: {
          ...state.activePlan,
          stops: newStops,
          stopsMap: newStopsMap,
        },
      };
    }
    case PlanActionType.PLAN_STOP_RESCHEDULED: {
      const planStop = action.payload.stop as Stop;

      if (!(planStop.id in state.activePlan.stopsMap)) return state;

      const newStopsMap = { ...state.activePlan.stopsMap };
      newStopsMap[planStop.id] = planStop;
      const newStops = Object.values(newStopsMap);

      // Update the drivers stops
      let updatedDrivers: Driver[] = state.activePlan.drivers;

      // From assigned to unassigned
      if (!planStop.driver_id) {
        updatedDrivers = state.activePlan.drivers.map((driver) => {
          driver.stops = driver.stops.map((driverStop) => {
            if (driverStop.id === planStop.id) {
              return planStop;
            }
            return driverStop;
          });
          return driver;
        });
      } else {
        updatedDrivers = state.activePlan.drivers.map((driver) => {
          // Updating the actual stop driver
          if (planStop.driver_id === driver.id) {
            // The new stop driver is the same as the last one
            if (driver.stops.find((driverStop) => driverStop.id === planStop.id)) {
              driver.stops = driver.stops.map((driverStop) => {
                if (driverStop.id === planStop.id) {
                  return planStop;
                }
                return driverStop;
              });
            } else {
              // The new stop driver did not have the stop before
              driver.stops = [...driver.stops, planStop];
            }
            return driver;
          } else {
            // Updating the other drivers that could have that stop
            driver.stops = [...driver.stops.filter((driverStop) => driverStop.id !== planStop.id)];
          }
          return driver;
        });
      }

      const driversMap = arrayToMap(updatedDrivers);

      return {
        ...state,
        activePlan: {
          ...state.activePlan,
          stops: newStops,
          stopsMap: newStopsMap,
          drivers: updatedDrivers,
          driversMap,
        },
      };
    }
    case PlanActionType.UPDATE_PLAN_DRIVER_LAST_POSITION: {
      const lastPosition = action.payload.lastPosition as GPSTrack | undefined;
      const driverId = action.payload.driverId as string;

      if (!(driverId in state.activePlan.driversMap)) return state;

      const newDriversMap = { ...state.activePlan.driversMap };
      newDriversMap[driverId].last_position = lastPosition;
      const newDrivers = Object.values(newDriversMap);
      return {
        ...state,
        activePlan: {
          ...state.activePlan,
          drivers: newDrivers,
          driversMap: newDriversMap,
        },
      };
    }
    case PlanActionType.UPDATE_PLAN_DRIVER_STATUS: {
      const status = action.payload.status as DriverStatus;
      const driverId = action.payload.driverId as string;

      if (!(driverId in state.activePlan.driversMap)) return state;

      const newDriversMap = { ...state.activePlan.driversMap };
      newDriversMap[driverId].status = status;
      const newDrivers = Object.values(newDriversMap);
      return {
        ...state,
        activePlan: {
          ...state.activePlan,
          drivers: newDrivers,
          driversMap: newDriversMap,
        },
      };
    }
    case PlanActionType.CREATE_PLAN_STOP: {
      const planStop = action.payload.stop;
      if (planStop.id in state.activePlan.stopsMap) return state;

      const newStopsMap = { ...state.activePlan.stopsMap };
      newStopsMap[planStop.id] = planStop;
      const newStops = Object.values(newStopsMap);
      return {
        ...state,
        activePlan: {
          ...state.activePlan,
          stops: newStops,
          stopsMap: newStopsMap,
        },
      };
    }
    case PlanActionType.DELETE_PLAN_STOP: {
      const planStop = action.payload.stop as Stop;

      if (!(planStop.id in state.activePlan.stopsMap)) return state;

      const newStopsMap = { ...state.activePlan.stopsMap };
      delete newStopsMap[planStop.id];
      const newStops = Object.values(newStopsMap);
      return {
        ...state,
        activePlan: {
          ...state.activePlan,
          stops: newStops,
          stopsMap: newStopsMap,
        },
      };
    }
    case PlanActionType.CREATE_PLAN_DRIVER: {
      const planDriver = action.payload.driver;
      if (planDriver.id in state.activePlan.driversMap) return state;

      const newDriversMap = { ...state.activePlan.driversMap };
      newDriversMap[planDriver.id] = planDriver;
      const newDrivers = Object.values(newDriversMap);
      return {
        ...state,
        activePlan: {
          ...state.activePlan,
          drivers: newDrivers,
          driversMap: newDriversMap,
        },
      };
    }
    case PlanActionType.DELETE_PLAN_DRIVER: {
      const planDriver = action.payload.driver;
      if (!(planDriver.id in state.activePlan.driversMap)) return state;

      const newDriversMap = { ...state.activePlan.driversMap };
      const stops = newDriversMap[planDriver.id].stops;
      delete newDriversMap[planDriver.id];
      const newStopsMap = { ...state.activePlan.stopsMap };
      stops.forEach((stop) => {
        newStopsMap[stop.id].unassign();
      });

      const newDrivers = Object.values(newDriversMap);
      const newStops = Object.values(newStopsMap);
      return {
        ...state,
        activePlan: {
          ...state.activePlan,
          drivers: newDrivers,
          driversMap: newDriversMap,
          stops: newStops,
          stopsMap: newStopsMap,
        },
      };
    }
    case PlanActionType.SET_ACTIVE_PLAN_MAP_FILTERS: {
      setActivePlanMapSymbolFilters({ symbols: action.payload.mapFilters.symbols });
      return {
        ...state,
        activePlan: {
          ...state.activePlan,
          mapFilters: action.payload.mapFilters,
        },
      };
    }
    case PlanActionType.SET_ACTIVE_PLAN_FOCUSED_STOP: {
      return {
        ...state,
        activePlan: {
          ...state.activePlan,
          focusedStop: action.payload.focusedStop,
        },
      };
    }
    default:
      return state;
  }
};
